import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Users, Building2, MapPin, BarChart3 } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#818CF8', '#00BEA8', '#FBBF24', '#EF4444', '#4ADE80', '#60A5FA', '#F472B6', '#A78BFA', '#FB923C'];
const STATUS_COLORS = { Enrolled: '#4ADE80', Hired: '#F97316', Applied: '#818CF8', Accepted: '#FBBF24' };

const Partnerships = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearFilter, setYearFilter] = useState('All');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/.netlify/functions/partnerships-data');
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (err) {
      console.error('Partnerships fetch failed:', err);
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="hf-container">
        <div className="hf-map-card" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
          <RefreshCw size={24} className="hf-spinner" style={{ marginBottom: '0.75rem' }} />
          <div style={{ color: 'rgba(199, 210, 254, 0.7)', fontSize: '0.9rem' }}>Loading partnership data from Salesforce...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hf-container">
        <div className="hf-map-card" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ color: '#EF4444', marginBottom: '0.75rem', fontSize: '1rem' }}>Failed to load partnership data</div>
          <div style={{ color: 'rgba(199, 210, 254, 0.5)', marginBottom: '1rem', fontSize: '0.8rem' }}>{error}</div>
          <button onClick={fetchData} className="hf-zoom-out-btn" style={{ position: 'relative' }}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const segments = data.segments || {};
  const yearOptions = ['All', ...(data.years || [])];
  const seg = segments[yearFilter] || segments.All || {
    totalCandidates: 0, activePartners: 0, totalStates: 0,
    partnershipSummary: [], stateSummary: [], statusSummary: [], partnerStateMatrix: [],
  };

  const enrolledTotal = seg.statusSummary.find(s => s.status === 'Enrolled')?.count || 0;
  const hiredTotal = seg.statusSummary.find(s => s.status === 'Hired')?.count || 0;

  return (
    <div className="hf-container">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>
            Partnerships
          </h2>
          <p style={{ color: 'rgba(199, 210, 254, 0.7)', fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>
            Active Teacher candidates by Partnership — ESS, SESI, Teach Start, University Instructors, NTL, TFA, Phalen Academies, Academica
          </p>
          {data.fetchedAt && (
            <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.35)' }}>
              Last synced: {new Date(data.fetchedAt).toLocaleString()}
            </span>
          )}
        </div>
        <button onClick={fetchData} style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          background: 'rgba(0, 190, 168, 0.15)', border: '1px solid rgba(0, 190, 168, 0.3)',
          color: '#4ADE80', borderRadius: '6px', padding: '0.4rem 0.75rem',
          fontSize: '0.78rem', cursor: 'pointer',
        }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Year Filter Pills */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {yearOptions.map(y => {
          const active = yearFilter === y;
          return (
            <button
              key={y}
              onClick={() => setYearFilter(y)}
              style={{
                padding: '0.35rem 0.9rem',
                borderRadius: '999px',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: active ? '1px solid rgba(129, 140, 248, 0.6)' : '1px solid rgba(255,255,255,0.1)',
                background: active ? 'rgba(129, 140, 248, 0.2)' : 'rgba(255,255,255,0.03)',
                color: active ? '#C7D2FE' : 'rgba(199, 210, 254, 0.6)',
                transition: 'all 0.15s ease',
              }}
            >
              {y === 'All' ? 'All' : y}
            </button>
          );
        })}
      </div>

      {/* KPI Cards */}
      <div className="hf-kpi-row" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #818CF8' }}>
          <div className="hf-kpi-value" style={{ color: '#818CF8' }}>{seg.activePartners}</div>
          <div className="hf-kpi-label">Active Partners</div>
          <div className="hf-kpi-sub">with candidates</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #00BEA8' }}>
          <div className="hf-kpi-value" style={{ color: '#00BEA8' }}>{seg.totalCandidates.toLocaleString()}</div>
          <div className="hf-kpi-label">Total Candidates</div>
          <div className="hf-kpi-sub">in partnerships</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #4ADE80' }}>
          <div className="hf-kpi-value" style={{ color: '#4ADE80' }}>{enrolledTotal.toLocaleString()}</div>
          <div className="hf-kpi-label">Enrolled</div>
          <div className="hf-kpi-sub">{seg.totalCandidates > 0 ? `${(enrolledTotal / seg.totalCandidates * 100).toFixed(1)}%` : '—'}</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #F97316' }}>
          <div className="hf-kpi-value" style={{ color: '#F97316' }}>{hiredTotal.toLocaleString()}</div>
          <div className="hf-kpi-label">Hired</div>
          <div className="hf-kpi-sub">{seg.totalCandidates > 0 ? `${(hiredTotal / seg.totalCandidates * 100).toFixed(1)}%` : '—'}</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #60A5FA' }}>
          <div className="hf-kpi-value" style={{ color: '#60A5FA' }}>{seg.totalStates}</div>
          <div className="hf-kpi-label">States</div>
          <div className="hf-kpi-sub">with partnership candidates</div>
        </div>
      </div>

      {/* Partnership Breakdown + Pie Chart */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {/* Per-Partnership Bar Chart */}
        <div className="hf-map-card">
          <h3 className="hf-section-title">
            <Building2 size={18} />
            Candidates by Partnership
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={seg.partnershipSummary} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="name" stroke="rgba(199,210,254,0.4)" fontSize={11} />
              <YAxis stroke="rgba(199,210,254,0.4)" fontSize={11} />
              <Tooltip
                contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px' }}
                labelStyle={{ color: '#E0E7FF' }}
              />
              <Legend />
              <Bar dataKey="enrolled" name="Enrolled" fill="#4ADE80" radius={[4, 4, 0, 0]} />
              <Bar dataKey="hired" name="Hired" fill="#F97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Admission Status Pie */}
        <div className="hf-map-card">
          <h3 className="hf-section-title">
            <BarChart3 size={18} />
            Admission Status Breakdown
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={seg.statusSummary}
                dataKey="count"
                nameKey="status"
                cx="50%"
                cy="50%"
                outerRadius={90}
                label={({ status, count, percent }) => `${status}: ${count} (${(percent * 100).toFixed(0)}%)`}
                labelLine={{ stroke: 'rgba(199,210,254,0.3)' }}
              >
                {seg.statusSummary.map((entry, i) => (
                  <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Partnership Detail Table */}
      <div className="hf-map-card">
        <h3 className="hf-section-title">
          <Building2 size={18} />
          Partnership Detail
        </h3>
        <div style={{ fontSize: '0.8rem' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '2fr 80px 80px 80px 80px',
            padding: '0.5rem 0.75rem', fontWeight: 600, fontSize: '0.68rem',
            color: 'rgba(199,210,254,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}>
            <span>Partnership</span>
            <span style={{ textAlign: 'center' }}>Total</span>
            <span style={{ textAlign: 'center' }}>Enrolled</span>
            <span style={{ textAlign: 'center' }}>Hired</span>
            <span style={{ textAlign: 'center' }}>States</span>
          </div>
          {seg.partnershipSummary.map((p, i) => (
            <div key={p.name} style={{
              display: 'grid', gridTemplateColumns: '2fr 80px 80px 80px 80px',
              padding: '0.45rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.03)',
              alignItems: 'center',
            }}>
              <span style={{ color: 'rgba(199,210,254,0.9)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                {p.name}
              </span>
              <span style={{ textAlign: 'center', color: '#818CF8', fontWeight: 700 }}>{p.total}</span>
              <span style={{ textAlign: 'center', color: '#4ADE80' }}>{p.enrolled}</span>
              <span style={{ textAlign: 'center', color: '#F97316' }}>{p.hired}</span>
              <span style={{ textAlign: 'center', color: 'rgba(199,210,254,0.6)' }}>{p.stateCount}</span>
            </div>
          ))}
          <div style={{
            display: 'grid', gridTemplateColumns: '2fr 80px 80px 80px 80px',
            padding: '0.5rem 0.75rem', borderTop: '2px solid rgba(255,255,255,0.1)',
            fontWeight: 700,
          }}>
            <span style={{ color: '#fff' }}>TOTAL</span>
            <span style={{ textAlign: 'center', color: '#818CF8' }}>{seg.totalCandidates}</span>
            <span style={{ textAlign: 'center', color: '#4ADE80' }}>{enrolledTotal}</span>
            <span style={{ textAlign: 'center', color: '#F97316' }}>{hiredTotal}</span>
            <span style={{ textAlign: 'center', color: 'rgba(199,210,254,0.6)' }}>{seg.totalStates}</span>
          </div>
        </div>
      </div>

      {/* State Breakdown */}
      <div className="hf-map-card">
        <h3 className="hf-section-title">
          <MapPin size={18} />
          Candidates by State
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {/* State bar chart */}
          <div>
            <ResponsiveContainer width="100%" height={Math.max(250, seg.stateSummary.length * 28)}>
              <BarChart data={seg.stateSummary} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="rgba(199,210,254,0.4)" fontSize={11} />
                <YAxis type="category" dataKey="state" stroke="rgba(199,210,254,0.4)" fontSize={11} width={100} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px' }}
                  labelStyle={{ color: '#E0E7FF' }}
                />
                <Legend />
                <Bar dataKey="enrolled" name="Enrolled" fill="#4ADE80" radius={[0, 4, 4, 0]} stackId="a" />
                <Bar dataKey="hired" name="Hired" fill="#F97316" radius={[0, 4, 4, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* State table */}
          <div style={{ fontSize: '0.78rem', maxHeight: '400px', overflowY: 'auto' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 65px 65px 65px',
              padding: '0.4rem 0.6rem', fontWeight: 600, fontSize: '0.65rem',
              color: 'rgba(199,210,254,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              position: 'sticky', top: 0, background: 'rgba(15, 23, 42, 0.95)',
            }}>
              <span>State</span>
              <span style={{ textAlign: 'center' }}>Total</span>
              <span style={{ textAlign: 'center' }}>Enrolled</span>
              <span style={{ textAlign: 'center' }}>Hired</span>
            </div>
            {seg.stateSummary.map(row => (
              <div key={row.state} style={{
                display: 'grid', gridTemplateColumns: '1fr 65px 65px 65px',
                padding: '0.35rem 0.6rem', borderBottom: '1px solid rgba(255,255,255,0.03)',
              }}>
                <span style={{ color: 'rgba(199,210,254,0.9)', fontWeight: 600 }}>{row.state}</span>
                <span style={{ textAlign: 'center', color: '#818CF8' }}>{row.total}</span>
                <span style={{ textAlign: 'center', color: '#4ADE80' }}>{row.enrolled}</span>
                <span style={{ textAlign: 'center', color: '#F97316' }}>{row.hired}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Per-Partnership Geographic Breakdown */}
      <div className="hf-map-card">
        <h3 className="hf-section-title">
          <MapPin size={18} />
          Partnership × State Detail
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          {seg.partnerStateMatrix.filter(p => p.states.length > 0).map((p, i) => (
            <div key={p.name} style={{
              background: 'rgba(255,255,255,0.02)', borderRadius: '8px', padding: '0.75rem',
              border: '1px solid rgba(255,255,255,0.05)',
            }}>
              <div style={{ fontWeight: 700, color: COLORS[i % COLORS.length], fontSize: '0.85rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: COLORS[i % COLORS.length] }} />
                {p.name}
                <span style={{ color: 'rgba(199,210,254,0.4)', fontWeight: 400, fontSize: '0.72rem', marginLeft: 'auto' }}>
                  {p.states.reduce((s, st) => s + st.count, 0)} total
                </span>
              </div>
              {p.states.map(st => (
                <div key={st.state} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', fontSize: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <span style={{ color: 'rgba(199,210,254,0.7)' }}>{st.state}</span>
                  <span style={{ color: 'rgba(199,210,254,0.9)', fontWeight: 600 }}>{st.count}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Partnerships;
